
import { getCache, setCacheEntry, TTL_GEO } from '../cache/secureStorage';
import { getGlobalCache, setGlobalCache } from '../supabaseCache';
import { getHereGeocodingViaEdge, getHereReverseGeocodingViaEdge } from '../hereMaps';

declare var google: any;

const GEO_CACHE_LOCAL_KEY = 'sal_geo_cache_v1';
const GEO_CACHE_KIND = 'geocode';

/**
 * Normaliza o endereço para maximizar hit rate do cache.
 */
export const normalizeAddress = (addr: string): string => {
    return addr.toLowerCase()
        .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
        .replace(/\bav\b|\bavenida\b/g, 'av')
        .replace(/\br\b|\brua\b/g, 'rua')
        .replace(/\bedf\b|\bedificio\b/g, 'edf')
        .replace(/[^a-z0-9]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
};

/**
 * v44.0: seedGeocodingCache - Inteligência Gratuita
 * Semeia o cache com coordenadas que já vêm de APIs externas (CardapioWeb, etc)
 */
export const seedGeocodingCache = async (address: string, lat: number, lng: number) => {
    if (!address || !lat || !lng) return;

    const normalizedKey = normalizeAddress(address);
    const safeKey = btoa(unescape(encodeURIComponent(normalizedKey))).substring(0, 100);
    const data = { lat, lng };

    setCacheEntry(GEO_CACHE_LOCAL_KEY, normalizedKey, data, TTL_GEO);

    try {
        await setGlobalCache(`geo_${safeKey}`, GEO_CACHE_KIND, {
            ...data,
            address,
            normalizedKey,
            updatedAt: Date.now(),
            source: 'seeded_free'
        });
    } catch (e) {
        console.error("[GeoSeed] Falha ao semear cache:", e);
    }
};

export const geocodeAddress = async (
    address: string | { street: string, neighborhood: string, city: string, state: string },
    hubLat?: number,
    hubLng?: number,
    googleApiKey?: string,
    hereApiKey?: string
): Promise<{ lat: number, lng: number } | null> => {
    let query = typeof address === 'string' ? address : `${address.street}, ${address.neighborhood}, ${address.city} - ${address.state}`;
    const normalizedKey = normalizeAddress(query);
    const safeKey = btoa(unescape(encodeURIComponent(normalizedKey))).substring(0, 100);

    const localCache = getCache(GEO_CACHE_LOCAL_KEY, TTL_GEO);
    if (localCache[normalizedKey]) return localCache[normalizedKey].data;

    try {
        const cached = await getGlobalCache<{ lat: number; lng: number }>(`geo_${safeKey}`);
        if (cached) {
            setCacheEntry(GEO_CACHE_LOCAL_KEY, normalizedKey, cached, TTL_GEO);
            return cached;
        }
    } catch (e) { console.error("Cache Read Fail", e); }

    // 1. TENTA HERE MAPS GEOCODING (PRIMARY) via Edge Function
    if (hereApiKey) {
        try {
            const hereResult = await getHereGeocodingViaEdge(query, hereApiKey);
            if (hereResult && hereResult.lat && hereResult.lng) {
                const result = { lat: hereResult.lat, lng: hereResult.lng };

                setCacheEntry(GEO_CACHE_LOCAL_KEY, normalizedKey, result, TTL_GEO);
                try {
                    await setGlobalCache(`geo_${safeKey}`, GEO_CACHE_KIND, {
                        ...result,
                        address: query,
                        normalizedKey,
                        updatedAt: Date.now(),
                        source: 'here_api'
                    });
                } catch (e) { }

                return result;
            }
        } catch (e: any) {
            console.error("[Geocoding] HERE Error:", e.message);
        }
    }

    // 2. FALLBACK: GOOGLE MAPS GEOCODING
    if (!googleApiKey) {
        // Sem API key: retorna posição aleatória próxima do hub
        if (hubLat && hubLng) {
            const r = 0.01 * Math.sqrt(Math.random());
            const theta = Math.random() * 2 * Math.PI;
            return { lat: hubLat + r * Math.cos(theta), lng: hubLng + r * Math.sin(theta) };
        }
        return null;
    }

    try {
        const response = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(query)}&key=${googleApiKey}`);
        const data = await response.json();
        if (data.status === 'OK' && data.results.length > 0) {
            const location = data.results[0].geometry.location;
            const result = { lat: location.lat, lng: location.lng };

            setCacheEntry(GEO_CACHE_LOCAL_KEY, normalizedKey, result, TTL_GEO);
            try {
                await setGlobalCache(`geo_${safeKey}`, GEO_CACHE_KIND, {
                    ...result,
                    address: query,
                    normalizedKey,
                    updatedAt: Date.now(),
                    source: 'google_api'
                });
            } catch (e) { }

            return result;
        }
        console.warn('[Geocoding] Google retornou status:', data.status);
        return null;
    } catch (e) { 
        console.error("[Geocoding] Google Exception:", e);
        return null; 
    }
};

export const reverseGeocode = async (
    lat: number, 
    lng: number, 
    googleApiKey?: string,
    hereApiKey?: string
): Promise<string | null> => {
    const key = `rev_${lat.toFixed(4)},${lng.toFixed(4)}`;
    const safeKey = btoa(key);

    const localCache = getCache(GEO_CACHE_LOCAL_KEY, TTL_GEO);
    if (localCache[key]) return localCache[key].data;

    try {
        const cached = await getGlobalCache<{ address: string }>(`rev_${safeKey}`);
        if (cached?.address) {
            setCacheEntry(GEO_CACHE_LOCAL_KEY, key, cached.address, TTL_GEO);
            return cached.address;
        }
    } catch (e) { }

    // Sem API key: retorna coordenadas formatadas
    if (!googleApiKey && !hereApiKey) {
        return `Local (${lat.toFixed(4)}, ${lng.toFixed(4)})`;
    }

    // 1. TENTA HERE MAPS REVERSE GEOCODING (PRIMARY) via Edge Function
    if (hereApiKey) {
        try {
            const hereResult = await getHereReverseGeocodingViaEdge(lat, lng, hereApiKey);
            if (hereResult && hereResult.address) {
                const address = hereResult.address;

                setCacheEntry(GEO_CACHE_LOCAL_KEY, key, address, TTL_GEO);
                try {
                    await setGlobalCache(`rev_${safeKey}`, 'reverse_geocode', {
                        address,
                        lat, lng,
                        updatedAt: Date.now(),
                        source: 'here_api'
                    });
                } catch (e) { }

                return address;
            }
        } catch (e: any) {
            console.error("[ReverseGeocoding] HERE Error:", e.message);
        }
    }

    // 2. FALLBACK: GOOGLE MAPS REVERSE GEOCODING
    if (!googleApiKey) {
        return `Local (${lat.toFixed(4)}, ${lng.toFixed(4)})`;
    }

    try {
        const response = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${googleApiKey}`);
        const data = await response.json();
        if (data.status === 'OK' && data.results.length > 0) {
            const addr = data.results[0].formatted_address;
            setCacheEntry(GEO_CACHE_LOCAL_KEY, key, addr, TTL_GEO);
            try {
                await setGlobalCache(`rev_${safeKey}`, 'reverse_geocode', {
                    address: addr,
                    lat, lng,
                    updatedAt: Date.now(),
                    source: 'google_api'
                });
            } catch (e) { }
            return addr;
        }
        console.warn('[ReverseGeocoding] Google retornou status:', data.status);
        return null;
    } catch (e) { 
        console.error("[ReverseGeocoding] Google Exception:", e);
        return null; 
    }
};
